import * as React from "react"
import * as Yup from "yup"
import { FormikConfig } from "formik"
import {
  Link,
  Button,
  SelectConnectedField,
  StyledSelectOption,
  EmptyState,
  InputConnectedField,
  StyledModalActions,
} from "gatsby-interface"
import { MdArrowForward } from "react-icons/md"
import { Form, FormElement } from "@modules/form/components/Form"
import {
  firebaseIntegrationConfig as text,
  ui as uiText,
} from "@modules/locales/default.js"
import { FirebaseSite } from "@modules/graphql/types"
import { FormattedMessage } from "@modules/locales"

type FormValues = {
  firebaseSiteId: string
  firebaseAppName: string
}

const validationSchema = Yup.object().shape<FormValues>({
  firebaseSiteId: Yup.string().required(uiText.messages.validationIsRequired),
  firebaseAppName: Yup.string(),
})

export type FirebaseIntegrationFormProps = {
  initialValues: FormValues
  projects: FirebaseSite[]
  noProjects?: boolean
  onSubmit: (values: {
    projectId: string
    projectName: string
    appName: string
  }) => void
  onCancel: () => void
}

export function FirebaseIntegrationForm({
  initialValues,
  projects,
  noProjects,
  onSubmit,
  onCancel,
}: FirebaseIntegrationFormProps) {
  const projectOptions = React.useMemo<StyledSelectOption[]>(() => {
    return [{ value: ``, label: text.labels.selectProject }].concat(
      projects.map(project => ({
        value: project.id,
        label: project.name,
      }))
    )
  }, [projects])

  const handleSubmit: FormikConfig<FormValues>["onSubmit"] = values => {
    const selectedProject = projects.find(
      ({ id }) => id === values.firebaseSiteId
    )

    if (!selectedProject) {
      return
    }

    return onSubmit({
      projectId: selectedProject.id,
      projectName: selectedProject.name,
      appName: values.firebaseAppName,
    })
  }

  return (
    <Form<FormValues>
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      initialValues={initialValues}
    >
      {({ isSubmitting }) => (
        <FormElement applySpacing>
          {noProjects ? (
            <EmptyState
              heading={text.headers.noProjects}
              text={
                <FormattedMessage<never, "link">
                  message={text.messages.projectCreationInstructions}
                  tags={{
                    link: function(content) {
                      return (
                        <Link
                          variant="SIMPLE"
                          href="https://console.firebase.google.com"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {content}
                        </Link>
                      )
                    },
                  }}
                />
              }
            />
          ) : (
            <React.Fragment>
              <SelectConnectedField
                name="firebaseSiteId"
                label={text.labels.firebaseProject}
                options={projectOptions}
              />
              <InputConnectedField
                name="firebaseAppName"
                label={text.labels.firebaseSiteName}
              />
            </React.Fragment>
          )}
          <StyledModalActions>
            <Button variant="SECONDARY" type={`reset`} onClick={onCancel}>
              {uiText.actions.cancel}
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              rightIcon={<MdArrowForward />}
            >
              {uiText.actions.save}
            </Button>
          </StyledModalActions>
        </FormElement>
      )}
    </Form>
  )
}
